import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { Paperclip, Upload, X } from "lucide-react";
import { FranklinIcon } from "@/components/ui/franklin-icon";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FileTypeIcon } from "@/components/ui/documents/file-type-icon";
import { DocumentPicker, type PickerDocument } from "@/components/documents/DocumentPicker";
import { useDocumentUpload } from "@/hooks/useDocumentUpload";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { cn } from "@/lib/utils";
import { AttachMenuItems } from "./AttachMenuItems";
import { PanelSectionLabel } from "./PanelSectionLabel";

export interface StagedAttachment {
  id: number;
  filename: string;
  /**
   * Uploaded from here, rather than picked from Franklin: the document was
   * created for this attachment, so an issue draft that is discarded can put it
   * in the bin again instead of leaving it orphaned in "Issue Attachments".
   */
  uploaded?: boolean;
}

/** Imperative handle exposed when `hideActions` is set — lets a consumer
 *  (e.g. the comment composer) drive picking/uploading from its own UI. */
export interface IssueAttachmentsStagerHandle {
  openPicker: () => void;
  openUpload: () => void;
}

interface IssueAttachmentsStagerProps {
  /** Stable list of documents the user has chosen to attach. */
  value: StagedAttachment[];
  /**
   * Takes a functional update, not just a value: an upload resolves after the
   * render whose `value` this component closed over, so appending to that
   * snapshot would drop everything staged since. Multi-file uploads depend on
   * it — every file but the last was lost.
   */
  onChange: Dispatch<SetStateAction<StagedAttachment[]>>;
  buildingId: string;
  /**
   * Project the upload should land in. Required for uploads (the storage
   * helper rejects without one). If empty, the upload button is disabled.
   */
  projectId: string;
  /**
   * Document ids already linked elsewhere (e.g. an issue being edited) that
   * the picker should mark as already-attached and not re-add. Defaults to
   * the staged ids so a user can't pick the same Franklin doc twice.
   */
  additionalAttachedIds?: string[];
  /**
   * Storage path prefix for uploaded files. Defaults to `["issues"]`; the
   * message composer passes `["messages"]` so board attachments are grouped
   * separately in Storage.
   */
  storagePathSegments?: string[];
  /**
   * Project folder uploads are filed into. PM issue surfaces pass
   * `ISSUE_ATTACHMENTS_FOLDER_NAME` so an attachment made here lands in the
   * same single system folder as one made from the issue's attach menu.
   * Left unset (the message board) files land at the project root, as before.
   */
  targetFolderName?: string;
  /** Hide the section heading when the consumer already provides a label. */
  hideLabel?: boolean;
  /**
   * Hide the built-in "Add from Franklin" / "Upload" buttons. The consumer
   * drives picking/uploading via the imperative handle instead (WHA-3432
   * comment composer).
   */
  hideActions?: boolean;
  /**
   * `panel` draws the band the way the issue detail panel's Documents section
   * does — label and count, a quiet "Attach" menu, file rows — so a new issue
   * reads like an existing one. `form` (the default) is the compact stager the
   * comment composer uses.
   */
  variant?: "form" | "panel";
  /**
   * Fires when an upload batch starts and when it has landed. The document is
   * staged only once it exists, so a parent that saves or leaves meanwhile
   * would go without it — the issue draft holds its leave until this is false.
   */
  onUploadingChange?: (uploading: boolean) => void;
  className?: string;
}

/**
 * Shared "attachments stager" used by the New Issue modal (WHA-3347) and the
 * comment composer (WHA-3376). It owns a small list of documents that will be
 * linked to a future parent (issue or comment) once that parent is created.
 *
 * Two ways to add: pick existing Franklin documents via DocumentPicker, or
 * upload new files via useDocumentUpload. Uploads still create document rows
 * immediately — only the junction-table link is deferred until the parent
 * exists, so the caller wires that up itself after persisting.
 */
export const IssueAttachmentsStager = forwardRef<
  IssueAttachmentsStagerHandle,
  IssueAttachmentsStagerProps
>(function IssueAttachmentsStager(
  {
    value,
    onChange,
    buildingId,
    projectId,
    additionalAttachedIds,
    storagePathSegments = ["issues"],
    targetFolderName,
    hideLabel,
    hideActions,
    variant = "form",
    onUploadingChange,
    className,
  },
  ref
) {
  const { t } = useLanguage();
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Queue of in-flight upload filenames, drained in FIFO order by
  // onDocumentCreated. The hook only hands us the document id, but
  // uploads are awaited sequentially so the filename queue stays aligned.
  const pendingUploadFilenamesRef = useRef<string[]>([]);

  const { handleFileUpload, isUploading } = useDocumentUpload({
    buildingId,
    projectId,
    requireProject: true,
    storagePathSegments,
    folderPathSegments: targetFolderName ? [targetFolderName] : undefined,
    onDocumentCreated: (documentId) => {
      const filename = pendingUploadFilenamesRef.current.shift() ?? "";
      onChange((prev) => [...prev, { id: documentId, filename, uploaded: true }]);
    },
  });

  // Across the whole batch, not per file: `isUploading` drops between files.
  const [batchUploading, setBatchUploading] = useState(false);
  const uploading = batchUploading || isUploading;
  const onUploadingChangeRef = useRef(onUploadingChange);
  onUploadingChangeRef.current = onUploadingChange;
  useEffect(() => {
    onUploadingChangeRef.current?.(uploading);
  }, [uploading]);

  const stagedIds = value.map((d) => String(d.id));
  const pickerExcludeIds = additionalAttachedIds
    ? [...additionalAttachedIds, ...stagedIds]
    : stagedIds;

  const handlePicked = (docs: PickerDocument[]) => {
    onChange((prev) => {
      const next = [...prev];
      for (const doc of docs) {
        const numericId = Number(doc.id);
        if (next.some((d) => d.id === numericId)) continue;
        next.push({ id: numericId, filename: doc.filename });
      }
      return next;
    });
    setIsPickerOpen(false);
  };

  const handleUploadClick = useCallback(() => {
    // The buttons and the menu item are disabled without a project; this
    // covers the imperative handle, whose consumer draws its own control.
    if (!projectId) return;
    fileInputRef.current?.click();
  }, [projectId]);

  useImperativeHandle(
    ref,
    () => ({
      openPicker: () => setIsPickerOpen(true),
      openUpload: handleUploadClick,
    }),
    [handleUploadClick]
  );

  const handleFilesSelected = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setBatchUploading(true);
    try {
      for (const file of Array.from(files)) {
        pendingUploadFilenamesRef.current.push(file.name);
        // Sequential uploads keep the filename queue aligned with the
        // onDocumentCreated callbacks; staging itself is a functional update,
        // so it does not depend on the parent having re-rendered in between.
        await handleFileUpload(file);
      }
    } finally {
      setBatchUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeAt = (idx: number) => {
    onChange((prev) => prev.filter((_, i) => i !== idx));
  };

  const uploadDisabled = !projectId || uploading;

  const fileInput = (
    <input
      ref={fileInputRef}
      type="file"
      multiple
      className="hidden"
      onChange={(e) => handleFilesSelected(e.target.files)}
    />
  );

  const picker = (
    <DocumentPicker
      open={isPickerOpen}
      onOpenChange={setIsPickerOpen}
      buildingId={buildingId}
      // WHA-3393: lock the picker to the issue's project so the user
      // can't reach documents in another project. When projectId is
      // empty DocumentPicker falls back to the dropdown UI on its own.
      projectId={projectId || undefined}
      alreadyAttachedIds={pickerExcludeIds}
      mode="multi"
      onSelect={handlePicked}
      title={t.pm.issues.detail.addFromFranklin}
      submitLabel={t.pm.issues.detail.addFromFranklin}
    />
  );

  if (variant === "panel") {
    // Mirrors IssueDocumentsSection: the band, its label and count, the quiet
    // attach menu on the right, and LinkedDocumentRow-shaped rows. The rows do
    // not open — nothing is linked yet — so they carry no button.
    return (
      <div className={cn("py-2.5", className)}>
        <div className="flex items-center justify-between gap-3">
          <PanelSectionLabel>
            {t.pm.issues.detail.documents}
            {value.length > 0 && <span> · {value.length}</span>}
          </PanelSectionLabel>
          {!hideActions && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 gap-1.5 px-2 text-xs text-muted-foreground hover:text-foreground"
                  disabled={uploading}
                >
                  <Paperclip className="h-3.5 w-3.5" />
                  {uploading ? t.pm.issues.detail.uploading : t.pm.issues.detail.attach}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <AttachMenuItems
                  onAddFromFranklin={() => setIsPickerOpen(true)}
                  onUpload={handleUploadClick}
                  uploadDisabledReason={projectId ? undefined : t.pm.issues.draft.noProject}
                />
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        {fileInput}
        {value.length > 0 && (
          <ul className="mt-1.5 space-y-0.5">
            {value.map((doc, idx) => (
              <li
                key={`${doc.id}-${idx}`}
                className="group flex items-center gap-2 rounded-md border border-transparent px-2 py-1.5 hover:border-foreground/40"
              >
                <FileTypeIcon filename={doc.filename} className="h-6 w-6" />
                <span className="min-w-0 flex-1 truncate text-xs" title={doc.filename}>
                  {doc.filename}
                </span>
                <button
                  type="button"
                  onClick={() => removeAt(idx)}
                  aria-label={`${t.pm.issues.detail.removeDocument}: ${doc.filename}`}
                  className="opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100"
                >
                  <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                </button>
              </li>
            ))}
          </ul>
        )}
        {picker}
      </div>
    );
  }

  return (
    <div className={cn("space-y-1.5", className)}>
      {!hideLabel && (
        <p className="text-xs font-medium text-foreground">{t.pm.issues.detail.linkedDocuments}</p>
      )}
      {/* 32px compact chrome — the upload control scale. Add-from-Franklin
          follows the upload button so the row stays one height. */}
      {!hideActions && (
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 text-xs"
            onClick={() => setIsPickerOpen(true)}
          >
            <FranklinIcon className="mr-1 h-3 w-3" />
            {t.pm.issues.detail.addFromFranklin}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 text-xs"
            onClick={handleUploadClick}
            disabled={uploadDisabled}
            title={!projectId ? t.pm.issues.draft.noProject : undefined}
          >
            <Upload className="mr-1 h-3 w-3" />
            {uploading ? t.pm.issues.detail.uploading : t.pm.issues.detail.uploadFiles}
          </Button>
        </div>
      )}
      {fileInput}

      {value.length > 0 && (
        <ul className="space-y-1">
          {value.map((doc, idx) => (
            <li
              key={`${doc.id}-${idx}`}
              className="group flex items-center gap-2 rounded px-2 py-1 hover:bg-muted/50"
            >
              <FileTypeIcon filename={doc.filename} className="h-4 w-4" />
              <span className="flex-1 truncate text-xs" title={doc.filename}>
                {doc.filename}
              </span>
              <button
                type="button"
                onClick={() => removeAt(idx)}
                aria-label={`Remove ${doc.filename}`}
                className="opacity-0 transition-opacity group-hover:opacity-100"
              >
                <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {picker}
    </div>
  );
});
